// src/components/editable/EditableTable.tsx
// Editable table that fetches field schema and renders appropriate inputs
// Supports adding new fields and creating new events

import {
  Close,
  EditOutlined,
  HelpOutline,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Lock,
  LockOpen,
  Person,
  SmartToy,
  OpenInNew,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { createData, deleteData, updateData } from "api/apiHelpers";
import { useFieldGroups } from "hooks/useFieldGroups";
import { useFieldSchema } from "hooks/useFieldSchema";
import { useNotification } from "hooks/useNotification";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "../../i18n";
import AddEventButton from "./AddEventButton";
import AddFieldButton from "./AddFieldButton";
import EditableCell from "./EditableCell";
import FullFieldModal from "./FullFieldModal";
import { ImageThumbnail } from "./ImageThumbnail";
import ImageLightbox from "./ImageLightbox";

// Static-like tables use PER-FIELD locks (cursor_logs/039): a single record
// whose individual fields can be protected from AI overwrite. They get inline
// per-field lock toggles and NO whole-row "record" lock. Event tables keep the
// row-level `is_locked` lock instead.
const STATIC_LIKE_KINDS = [
  "statictable",
  "patientprofiletable",
  "molecularprofiletable",
  "hematologicalprofiletable",
  "cancerprofiletable",
];

// Tables that carry PER-FIELD provenance server-side (cursor_logs/039 per-field
// locks + cursor_logs/040 per-field `human_edited_fields`). This is DELIBERATELY
// separate from STATIC_LIKE_KINDS: that set is strictly about LAYOUT (single
// record with inline per-field lock toggles vs multi-row event tables), whereas
// this set is about how the "edited/created by user" BADGE is derived. The
// universal event-style tables (adverse events, medication statements, trial
// enrolments) are multi-row (NOT static-like) yet the backend still tracks
// per-field ownership via `human_edited_fields` (set by the viewset's
// perform_create/perform_update for exactly the fields the human provided).
// Without this distinction the "show all fields" modal falls back to the
// record-level `source` and marks EVERY field as user-authored when a human
// only set two of them. Keep the two sets independent so a table can have
// per-field badges without gaining the static single-record layout (and its
// inline lock toggle) it does not use.
//
// NOTE: this is a SUPERSET of STATIC_LIKE_KINDS — every static-like table also
// has per-field provenance, so `statictable` is included to preserve its
// existing per-field badges when the badge source is driven by this set (rather
// than by the old `!!onToggleFieldLock` inference).
export const PER_FIELD_PROVENANCE_KINDS = [
  "statictable",
  "patientprofiletable",
  "molecularprofiletable",
  "hematologicalprofiletable",
  "cancerprofiletable",
  "trialenrollmenttable",
  "adverseeventtable",
  "medicationstatementtable",
  "diagnosistable",
  "procedurestable",
  "tumorassessmenttable",
  "diseasestatustable",
  "imagingtable",
  "systemictherapytable",
  "labresulttable",
  "familyhistorytable",
  "cancerobservationtable",
];

// Event-style UNIVERSAL tables: the two ClinicalEventTable-derived universal
// tables that hold MANY rows per patient (unlike the single-record profile
// tables patient/molecular/hematological/trial). The sidebar category "+" must
// CREATE A NEW record for these (creation-modal flow) rather than edit the
// singleton rows[0] the way the profile tables do. See DataPanel's
// category-vs-creation routing.
export const EVENT_STYLE_UNIVERSAL_KINDS = [
  "adverseeventtable",
  "medicationstatementtable",
  "diagnosistable",
  "procedurestable",
  "tumorassessmenttable",
  "diseasestatustable",
  "imagingtable",
  "systemictherapytable",
  "labresulttable",
  "familyhistorytable",
  "cancerobservationtable",
];

interface EditableTableProps {
  data: any[];
  domainKey: string;
  tableType:
    | "eventtable"
    | "statictable"
    | "patientprofiletable"
    | "molecularprofiletable"
    | "hematologicalprofiletable"
    | "cancerprofiletable"
    | "trialenrollmenttable"
    | "adverseeventtable"
    | "medicationstatementtable"
    | "diagnosistable"
    | "procedurestable"
    | "tumorassessmenttable"
    | "diseasestatustable"
    | "imagingtable"
    | "systemictherapytable"
    | "labresulttable"
    | "familyhistorytable"
    | "cancerobservationtable";
  /** Patient ID - required for creating new events */
  patientId?: number | string;
  transposed?: boolean;
  alwaysVisibleColumns?: string[];
  onDataChange?: (updatedData: any[]) => void;
  /** Called when a new event is created */
  onEventCreated?: (newEvent: any) => void;
  /** Render callback for external action buttons (placed in section headers).
   *  Receives openAllFields(rowIndex) so the parent can trigger the modal. */
  renderHeaderActions?: (actions: {
    openAllFields: (rowIndex?: number) => void;
  }) => React.ReactNode;
  /** Called (via useEffect) when the compact add-button node changes.
   *  Parent can use this to place the button in an external header. */
  onHeaderAddButton?: (node: React.ReactNode | null) => void;
  /** Color variant for the header add button — "onDark" for dark backgrounds */
  headerButtonVariant?: "default" | "onDark";
  /** When set, the table expands this event row and scrolls to it (sidebar navigation). */
  focusEventId?: string | number | null;
  /** When set alongside `focusEventId`, opens that record's detail modal deep-linked
   *  to this field-group section (sidebar record → field-group navigation), mirroring
   *  the per-section pencils. Falsy → the record is expanded inline (record-node click). */
  focusEventSection?: string | null;
  /** Bumped by the caller on every sidebar focus click so re-selecting the same
   *  record (or a different group of it) re-triggers the focus effect. */
  focusNonce?: number;
  /** Available option counts per referenced table, keyed by `reference_table_type`
   *  (e.g. `{ diagnosistable: 3 }`). A `reference` field is hidden entirely (label
   *  + control, across columns / expanded detail / all-fields modal) when its
   *  referenced table has <= 1 row, since there is nothing to disambiguate — e.g.
   *  a Procedure's `cancer` picker on a single-diagnosis patient. */
  referenceCounts?: Record<string, number>;
  /** Imaging rows (each carries a `cancer` FK id + resolved `image_url`) used to
   *  surface a per-lesion thumbnail gallery on a Diagnosis row's detail modal.
   *  Only consumed when `tableType === "diagnosistable"`; ignored otherwise. */
  imagingRows?: any[];
}

// Fields to exclude from editing (shown separately or system fields)
const EXCLUDED_FIELDS = [
  "id",
  "event_id",
  "created_at",
  "modified_at",
  "created_by",
  "batch_id",
  "event_no",
  "text",
  "table",
  "processing_task_id",
  "processing_status",
  "patient",
  "environments",
  "platform_user",
  "is_validated",
  "progress",
  "eventtable_ptr",
  "found_with_parent",
  "institution_id",
  "dynamic_entities",
  "starter",
  // Source tracking fields (displayed separately)
  "source",
  "is_locked",
  "last_ai_extraction",
  "source_documents",
  "locked_fields",
  "human_edited_fields",
  // Internal backfill/sync audit blob — never user-visible (CLAUDE.md rule).
  "migration_notes",
  // oncology-course anchor FK on the residue tables — relational, not clinical.
  "cancer", "cancer_id",
  // Imaging image-display wiring (P1): the source-image link + resolved URL are
  // rendered as a dedicated thumbnail column / modal preview, never as editable
  // fields or table columns.
  "image_document", "image_url",
];

// ─── Cell density (row heights) ────────────────────────────────────────────
// Single source of truth for EditableTable vertical sizing. To make every
// static / event / collapsed / expanded row shorter or taller, change these
// three values. CELL_PY and EXPANDED_CELL_PY are MUI spacing units (×8px);
// BADGE_SIZE is px and sets the row-height FLOOR (the source/lock badge is the
// tallest thing in the first column, so rows can't get shorter than it).
const CELL_PY = 0.5; // base cells: static rows + collapsed event ("header") rows
const EXPANDED_CELL_PY = 0.5; // cells revealed under an expanded event row
const BADGE_SIZE = 24; // source/lock provenance badge diameter (px)

// Normalize key names → "created_at" → "Created At"
const normalizeKeyName = (key: string) =>
  key
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));

// Get API endpoint for a table type
const getApiEndpoint = (domainKey: string, tableType: string): string => {
  // Universal tables (patientprofile, molecular) don't use domain prefix
  const isUniversal =
    tableType === "patientprofiletable" ||
    tableType === "molecularprofiletable" ||
    tableType === "hematologicalprofiletable" ||
    tableType === "cancerprofiletable" ||
    tableType === "trialenrollmenttable" ||
    tableType === "adverseeventtable" ||
    tableType === "medicationstatementtable" ||
    tableType === "diagnosistable" ||
    tableType === "procedurestable" ||
    tableType === "tumorassessmenttable" ||
    tableType === "diseasestatustable" ||
    tableType === "imagingtable" ||
    tableType === "systemictherapytable" ||
    tableType === "labresulttable" ||
    tableType === "familyhistorytable" ||
    tableType === "cancerobservationtable";

  if (isUniversal) {
    return `/${tableType}`;
  }

  // Cancer-specific tables use domain prefix: medical.cancer.skin → medical-cancer-skin
  const urlDomain = domainKey.replace(/\./g, "-");
  return `/${urlDomain}-${tableType}`;
};

const EditableTable: React.FC<EditableTableProps> = ({
  data = [],
  domainKey,
  tableType,
  patientId,
  transposed = false,
  alwaysVisibleColumns = [],
  onDataChange,
  onEventCreated,
  renderHeaderActions,
  onHeaderAddButton,
  headerButtonVariant = "default",
  focusEventId = null,
  focusEventSection = null,
  focusNonce = 0,
  referenceCounts,
  imagingRows,
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const notification = useNotification();
  const { t } = useTranslation();

  // Imaging rows carry a synced source image (one row == one disease image).
  // When true we render a leftmost thumbnail column (display-only; the URL/link
  // fields are hidden from every editable field list). Only the Imaging table
  // exposes `image_url`, so no other table is affected.
  const showImageColumn = tableType === "imagingtable";

  // In-app image lightbox (Imaging panel). Clicking a thumbnail opens a carousel
  // over ALL images in this Imaging table, positioned at the clicked one. The
  // ordered URL list mirrors the rendered rows (rows without an image are simply
  // absent), so the clicked row's index is a stable indexOf lookup.
  const [imageLightbox, setImageLightbox] = useState<{ open: boolean; index: number }>({
    open: false,
    index: 0,
  });

  // Static-like tables (single record, per-field locks) vs event tables
  // (multiple rows, whole-row lock). Drives which lock UI we render.
  const isStaticLike = STATIC_LIKE_KINDS.includes(tableType);
  // Whether the "show all fields" modal should derive per-field badges from
  // `human_edited_fields` (see PER_FIELD_PROVENANCE_KINDS). Independent from
  // `isStaticLike` (layout) and from the lock-toggle affordance.
  const hasPerFieldProvenance = PER_FIELD_PROVENANCE_KINDS.includes(tableType);

  const { schema, loading: schemaLoading } = useFieldSchema(
    domainKey,
    tableType
  );
  const {
    fieldGroups,
    getFieldsForEventType,
    getEventTypeCategories,
  } = useFieldGroups(domainKey, tableType);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  // Sidebar-driven record focus. Two modes, both re-triggered by `focusNonce`:
  //  • no section → expand the matching row inline + scroll (record-node click,
  //    the existing History event sub-item behavior).
  //  • with section → open the record's detail modal deep-linked to that
  //    field-group, reusing the same FullFieldModal path as the per-section
  //    pencils (universal event tables have no inline category headers to scroll
  //    to, so the modal is the field-group navigation target).
  useEffect(() => {
    if (focusEventId == null) return;
    const rows = localDataRef.current;
    if (!rows?.length) return;
    const idx = rows.findIndex(
      (row) => String(row.event_id ?? row.id) === String(focusEventId)
    );
    if (idx < 0) return;
    if (focusEventSection) {
      setFullFieldModal({ open: true, rowIndex: idx, initialSection: focusEventSection });
    } else {
      setExpandedRow(idx);
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-event-row-id="${focusEventId}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  }, [focusEventId, focusEventSection, focusNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const [localData, setLocalData] = useState(data);
  const [addedFields, setAddedFields] = useState<
    Record<string | number, string[]>
  >({});
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; rowIndex: number; rowId: number | string }>({ open: false, rowIndex: -1, rowId: 0 });
  const [fullFieldModal, setFullFieldModal] = useState<{
    open: boolean;
    rowIndex: number;
    /** Section title to deep-link to: the modal opens with all sections + sidebar
     *  visible, scrolled to (and highlighting) this one. Driven by the per-section
     *  pencils. */
    initialSection?: string;
  }>({ open: false, rowIndex: 0 });

  // Stable refs for volatile values — breaks dependency cascading through
  // handleCellSave / handleCreateEvent / headerAddButton → parent setState loop
  const onDataChangeRef = useRef(onDataChange);
  const onEventCreatedRef = useRef(onEventCreated);
  const onHeaderAddButtonRef = useRef(onHeaderAddButton);
  const localDataRef = useRef(localData);
  const handleAddFieldRef = useRef<(rowId: number | string, fieldName: string) => void>(() => {});
  const handleCreateEventRef = useRef<(eventType: string) => Promise<void>>(async () => {});
  useEffect(() => { onDataChangeRef.current = onDataChange; });
  useEffect(() => { onEventCreatedRef.current = onEventCreated; });
  useEffect(() => { onHeaderAddButtonRef.current = onHeaderAddButton; });
  useEffect(() => { localDataRef.current = localData; });

  // Sync local data with props
  React.useEffect(() => {
    setLocalData(data);
  }, [data]);

  // Reference fields with nothing to disambiguate: a `reference` field whose
  // referenced table has <= 1 row is hidden everywhere (columns, expanded
  // detail, all-fields modal) so it never surfaces a dead single-option picker.
  // Driven by `referenceCounts` (keyed by `reference_table_type`) from the parent.
  const hiddenReferenceFields = useMemo(() => {
    const hidden = new Set<string>();
    if (!schema?.fields) return hidden;
    for (const [key, info] of Object.entries(schema.fields)) {
      if (info?.type !== "reference") continue;
      const refType = info.reference_table_type;
      const count = refType ? referenceCounts?.[refType] : undefined;
      if (count != null && count <= 1) hidden.add(key);
    }
    return hidden;
  }, [schema, referenceCounts]);

  // Filter keys to show
  const filteredKeys = useMemo(() => {
    if (data.length === 0 || !data[0]) return [];
    return Object.keys(data[0]).filter(
      (key) => !EXCLUDED_FIELDS.includes(key) && !hiddenReferenceFields.has(key)
    );
  }, [data, hiddenReferenceFields]);

  // Backend-declared primary/identifying columns (PRIMARY_COLUMNS class attr).
  // These are the up-front columns the table author chose as the key identifiers
  // (e.g. a diagnosis's primary/metastasis/recurrence discriminator), so they
  // must surface reliably — same rationale as the `event_date` exception below.
  const primaryColumnSet = useMemo(
    () => new Set(schema?.primary_columns ?? []),
    [schema]
  );

  // Hide fields that are null for all records — EXCEPT `event_date`, the
  // date-anchor of every event row, and the backend-declared primary columns.
  // Those must stay visible even when empty so the user can read/fill them
  // (otherwise an un-dated event drops the whole date column, which surprised
  // Joan on Adverse Events, and an unclassified key column such as
  // `diagnosis_event_type` on legacy rows silently vanishes). cursor_logs/036 §25.
  const usableColumns = useMemo(() => {
    return filteredKeys.filter(
      (col) =>
        col === "event_date" ||
        primaryColumnSet.has(col) ||
        !data.every((row) => row[col] == null || row[col] === "")
    );
  }, [filteredKeys, data, primaryColumnSet]);

  // Visible vs expandable columns
  const visibleColumns = useMemo(() => {
    if (alwaysVisibleColumns.length > 0) {
      return usableColumns.filter((col) => alwaysVisibleColumns.includes(col));
    }
    // Per-table primary-columns override with first-4 fallback: when the table's
    // schema declares `primary_columns` (backend PRIMARY_COLUMNS class attr,
    // surfaced additively) show those in declared order, intersected with
    // usableColumns for safety. On very small screens keep the compact cap.
    // Any table that does NOT declare primary columns falls through to the exact
    // original behaviour (first 4, 2 on small), so this stays byte-compatible.
    const primary = schema?.primary_columns;
    if (primary && primary.length > 0) {
      const declared = primary.filter((col) => usableColumns.includes(col));
      if (declared.length > 0) {
        return isSmall ? declared.slice(0, 2) : declared;
      }
    }
    // Default: show first 3-4 columns
    return usableColumns.slice(0, isSmall ? 2 : 4);
  }, [usableColumns, alwaysVisibleColumns, isSmall, schema]);

  const expandableColumns = useMemo(() => {
    return usableColumns.filter((col) => !visibleColumns.includes(col));
  }, [usableColumns, visibleColumns]);

  // Handle save for a cell
  const handleCellSave = useCallback(
    async (rowId: number | string, fieldName: string, value: any) => {
      const endpoint = getApiEndpoint(domainKey, tableType);

      try {
        const response = await updateData<Record<string, any>, Record<string, any>>(
          `${endpoint}/${rowId}/`,
          { [fieldName]: value }
        );

        // Merge the full API response so derived fields (source, etc.) are up-to-date
        setLocalData((prev) =>
          prev.map((row) =>
            row.id === rowId || row.event_id === rowId
              ? { ...row, ...response }
              : row
          )
        );

        if (onDataChangeRef.current) {
          const updated = localData.map((row) =>
            row.id === rowId || row.event_id === rowId
              ? { ...row, ...response }
              : row
          );
          onDataChangeRef.current(updated);
        }
      } catch (err: any) {
        notification.error(t("editable.save_failed", "Failed to save {{field}}").replace("{{field}}", normalizeKeyName(fieldName)));
        throw err;
      }
    },
    [domainKey, tableType, localData, notification, t]
  );

  // Atomic multi-field PATCH — used by the inline event_type editor when the
  // user changes type and confirms the reset of orphaned fields. Sending
  // `{event_type: NEW, ...nulls}` in one request keeps the backend's trust
  // hierarchy guard from misclassifying the bulk-clear as a meta-only edit.
  const handleMultiCellSave = useCallback(
    async (rowId: number | string, updates: Record<string, any>) => {
      const endpoint = getApiEndpoint(domainKey, tableType);
      try {
        const response = await updateData<Record<string, any>, Record<string, any>>(
          `${endpoint}/${rowId}/`,
          updates
        );
        setLocalData((prev) =>
          prev.map((row) =>
            row.id === rowId || row.event_id === rowId
              ? { ...row, ...response }
              : row
          )
        );
        if (onDataChangeRef.current) {
          const updated = localData.map((row) =>
            row.id === rowId || row.event_id === rowId
              ? { ...row, ...response }
              : row
          );
          onDataChangeRef.current(updated);
        }
      } catch (err: any) {
        notification.error(
          t("editable.save_failed_generic", "Failed to save changes")
        );
        throw err;
      }
    },
    [domainKey, tableType, localData, notification, t]
  );

  // Handle toggling lock status on a record
  const handleToggleLock = useCallback(
    async (rowId: number | string, currentLocked: boolean) => {
      const endpoint = getApiEndpoint(domainKey, tableType);
      const newLocked = !currentLocked;

      try {
        const response = await updateData<Record<string, any>, Record<string, any>>(
          `${endpoint}/${rowId}/`,
          { is_locked: newLocked }
        );

        setLocalData((prev) =>
          prev.map((row) =>
            row.id === rowId || row.event_id === rowId
              ? { ...row, ...response }
              : row
          )
        );

        notification.success(newLocked ? t("editable.record_locked", "Record locked") : t("editable.record_unlocked", "Record unlocked"));
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setLocalData((prev) =>
            prev.filter((row) => row.id !== rowId && row.event_id !== rowId)
          );
          notification.error(t("editable.record_not_found", "Record no longer exists"));
        } else {
          notification.error(t("editable.toggle_lock_failed", "Failed to toggle lock"));
        }
      }
    },
    [domainKey, tableType, notification]
  );

  // Toggle a SINGLE field's lock (cursor_logs/039). Adds/removes the field name
  // from the row's `locked_fields` and PATCHes it. A locked field is never
  // overwritten by AI extraction; unlocking re-enables AI on that field.
  const handleToggleFieldLock = useCallback(
    async (rowId: number | string, fieldName: string, nextLocked: boolean) => {
      const endpoint = getApiEndpoint(domainKey, tableType);
      const row = localData.find(
        (r) => r.id === rowId || r.event_id === rowId
      );
      const current: string[] = (row?.locked_fields as string[]) || [];
      const newLockedFields = nextLocked
        ? Array.from(new Set([...current, fieldName]))
        : current.filter((f) => f !== fieldName);
      try {
        const response = await updateData<Record<string, any>, Record<string, any>>(
          `${endpoint}/${rowId}/`,
          { locked_fields: newLockedFields }
        );
        setLocalData((prev) =>
          prev.map((r) =>
            r.id === rowId || r.event_id === rowId ? { ...r, ...response } : r
          )
        );
        notification.success(
          nextLocked
            ? t("editable.field_locked", "Field locked")
            : t("editable.field_unlocked", "Field unlocked")
        );
      } catch (err: any) {
        notification.error(
          t("editable.toggle_field_lock_failed", "Failed to toggle field lock")
        );
      }
    },
    [domainKey, tableType, localData, notification, t]
  );

  // Get source icon and tooltip for a row
  const getSourceIndicator = (row: any) => {
    const source = row.source || "ai_extracted";
    const isLocked = row.is_locked || false;

    if (isLocked) {
      return {
        icon: <Lock sx={{ fontSize: 13 }} />,
        tooltip: t("editable.lock_tooltip", "Locked — AI will not modify this record"),
        bg: alpha(theme.palette.primary.dark, 0.18),
        color: alpha(theme.palette.primary.dark, 0.75),
      };
    }

    switch (source) {
      case "human_created":
        return {
          icon: <Person sx={{ fontSize: 13 }} />,
          tooltip: t("editable.source_user", "Created by user"),
          bg: alpha(theme.palette.info.main, 0.12),
          color: theme.palette.info.main,
        };
      case "human_edited":
        return {
          icon: <Person sx={{ fontSize: 13 }} />,
          tooltip: t("editable.source_edited", "Edited by user (AI extracted, then modified)"),
          bg: alpha(theme.palette.primary.main, 0.12),
          color: alpha(theme.palette.primary.main, 0.8),
        };
      case "ai_extracted":
      default:
        return {
          icon: <SmartToy sx={{ fontSize: 13 }} />,
          tooltip: t("editable.source_ai", "AI extracted"),
          bg: alpha(theme.palette.primary.dark, 0.12),
          color: alpha(theme.palette.primary.dark, 0.55),
        };
    }
  };

  // Per-field provenance for static-like tables (cursor_logs/040): the record
  // carries one row-level `source`, but per-field ownership lives in
  // `human_edited_fields` — the dedicated provenance list. Unlike `locked_fields`
  // (cleared on unlock), this stays set after a user unlocks a field, so the
  // field keeps its "edited by user" label until AI actually overwrites it. This
  // keeps editing ONE field from labelling every field as edited, and fixes the
  // bug where unlocking a human-edited field made it read as "AI extracted".
  const getStaticFieldIndicator = (record: any, key: string) => {
    if (!record) return null;
    if (record.is_locked) return getSourceIndicator(record); // whole-record lock
    const humanEdited =
      ((record.human_edited_fields as string[]) || []).includes(key);
    if (humanEdited) {
      // A human authored this field's value (true even after unlock). Use the
      // record's source to distinguish "created by user" from "edited by user".
      const src =
        (record.source || "ai_extracted") === "human_created"
          ? "human_created"
          : "human_edited";
      return getSourceIndicator({ source: src });
    }
    return getSourceIndicator({ source: "ai_extracted" });
  };

  // Get row ID (event tables use event_id, static use id)
  const getRowId = (row: any): number | string => {
    return row.event_id || row.id;
  };

  // Get all schema fields (excluding system fields)
  const allSchemaFields = useMemo(() => {
    if (!schema?.fields) return {};
    return Object.fromEntries(
      Object.entries(schema.fields).filter(
        ([key]) =>
          !EXCLUDED_FIELDS.includes(key) && !hiddenReferenceFields.has(key)
      )
    );
  }, [schema, hiddenReferenceFields]);

  // Handle adding a new field to a record
  const handleAddField = useCallback(
    async (rowId: number | string, fieldName: string) => {
      const endpoint = getApiEndpoint(domainKey, tableType);

      try {
        // Set field to null (will use Django default)
        await updateData(`${endpoint}/${rowId}/`, { [fieldName]: null });

        // Track that this field was added for this row
        setAddedFields((prev) => ({
          ...prev,
          [rowId]: [...(prev[rowId] || []), fieldName],
        }));

        // Update local data to include the field
        setLocalData((prev) =>
          prev.map((row) =>
            getRowId(row) === rowId ? { ...row, [fieldName]: null } : row
          )
        );

        notification.success(t("editable.field_added", "Added {{field}}").replace("{{field}}", normalizeKeyName(fieldName)));
      } catch (err: any) {
        notification.error(t("editable.add_field_failed", "Failed to add field"));
      }
    },
    [domainKey, tableType, notification]
  );
  handleAddFieldRef.current = handleAddField;

  // Handle creating a new event
  const handleCreateEvent = useCallback(
    async (eventType: string) => {
      if (!patientId) {
        notification.error(t("editable.patient_id_required", "Patient ID is required to create events"));
        return;
      }
      if (creatingEvent) return;

      const endpoint = getApiEndpoint(domainKey, tableType);
      setCreatingEvent(true);

      try {
        const newEvent = await createData<any, any>(endpoint + "/", {
          patient: patientId,
          event_type: eventType,
        });

        // Add to local data
        setLocalData((prev) => [...prev, newEvent]);

        const newIndex = localData.length;

        // Open the FullFieldModal for the new event
        setFullFieldModal({ open: true, rowIndex: newIndex });

        // Notify parent
        if (onEventCreatedRef.current) {
          onEventCreatedRef.current(newEvent);
        }
        if (onDataChangeRef.current) {
          onDataChangeRef.current([...localData, newEvent]);
        }

        notification.success(t("editable.event_created", "Created {{eventType}} event").replace("{{eventType}}", eventType.replace(/_/g, " ")));
      } catch (err: any) {
        notification.error(t("editable.create_event_failed", "Failed to create event"));
        throw err;
      } finally {
        setCreatingEvent(false);
      }
    },
    [
      patientId,
      domainKey,
      tableType,
      localData,
      creatingEvent,
      notification,
    ]
  );
  handleCreateEventRef.current = handleCreateEvent;

  const handleDeleteEvent = useCallback(
    async (rowId: number | string, rowIndex: number) => {
      const endpoint = getApiEndpoint(domainKey, tableType);
      try {
        await deleteData(`${endpoint}/${rowId}/`);
        const updated = localData.filter((_, i) => i !== rowIndex);
        setLocalData(updated);
        if (expandedRow === rowIndex) setExpandedRow(null);
        if (onDataChangeRef.current) onDataChangeRef.current(updated);
        notification.success(t("editable.event_deleted", "Event deleted"));
      } catch {
        notification.error(t("editable.delete_event_failed", "Failed to delete event"));
      }
      setDeleteConfirm({ open: false, rowIndex: -1, rowId: 0 });
    },
    [domainKey, tableType, localData, expandedRow, notification, t]
  );

  // Get fields currently shown for a row (existing + added)
  const getShownFieldsForRow = useCallback(
    (row: any) => {
      const rowId = getRowId(row);
      const existingFields = Object.keys(row).filter(
        (k) =>
          !EXCLUDED_FIELDS.includes(k) &&
          !hiddenReferenceFields.has(k) &&
          row[k] != null &&
          row[k] !== ""
      );
      const added = addedFields[rowId] || [];
      return [...new Set([...existingFields, ...added])].filter(
        (k) => !hiddenReferenceFields.has(k)
      );
    },
    [addedFields, hiddenReferenceFields]
  );

  // Fix B — LabResult compact-column fallbacks (display + edit target). A
  // qualitative row (e.g. ER/PR biomarkers) stores its assay in `analyte_name`
  // (with analyte = "other") and its result in `value_text` (Qualitative Result),
  // not the numeric `value`. The bare PRIMARY_COLUMNS
  // ["analyte","value"] therefore read as empty ("Other" + blank). For the
  // LabResult table ONLY, swap the effective field a compact cell renders AND
  // edits so the row shows its real content and edits still target the right
  // column. Per-row, so numeric rows are untouched; no stored data changes and no
  // other table is affected.
  //
  // The value fallback is `value_text` ONLY. A qualitative result (e.g.
  // "detected", "positive (Allred 8/8)") IS a legitimate result value, so it
  // belongs under the "Value" column. `interpretation` (positive/negative/
  // high/low) is a FLAG, not a measurement, so it must NEVER surface under a
  // column headed "Value" — that would mislabel a flag as the result.
  const resolveCompactColumnKey = useCallback(
    (row: any, key: string): string => {
      if (tableType !== "labresulttable") return key;
      const isEmpty = (v: any) => v == null || v === "";
      if (key === "analyte") {
        const a = row.analyte;
        if (
          (isEmpty(a) || String(a).toLowerCase() === "other") &&
          !isEmpty(row.analyte_name)
        ) {
          return "analyte_name";
        }
      } else if (key === "value") {
        if (isEmpty(row.value) && !isEmpty(row.value_text)) {
          return "value_text";
        }
      }
      return key;
    },
    [tableType]
  );

  const tableSxBase = {
    "& td, & th": {
      borderBottom: `1px solid ${theme.palette.divider}`,
      py: CELL_PY,
      px: 1.25,
      fontSize: "0.8rem",
      color: theme.palette.primary.dark,
      overflow: "hidden",
      textOverflow: "ellipsis",
      wordBreak: "break-word" as const,
    },
    "& thead th": {
      borderBottom: `2px solid ${theme.palette.divider}`,
      fontWeight: 600,
      fontSize: "0.8rem",
      color: theme.palette.primary.dark,
    },
  };
  const transposedTableSx = { ...tableSxBase, tableLayout: "fixed" as const, width: "100%" };
  const normalTableSx = { ...tableSxBase };

  // TableContainer background - matches NeumorphicCard dark variant
  const tableContainerSx = {
    overflowX: "auto",
    borderRadius: theme.shape.borderRadius / 6,
    background: `linear-gradient(180deg,
      ${alpha(theme.palette.primary.dark, 0.1)} 0%,
      ${alpha(theme.palette.primary.dark, 0.1)} 100%)`,
    border: `1px solid ${alpha(theme.palette.secondary.main, 0.55)}`,
    backdropFilter: "blur(8px)",
    // Narrow 3px scrollbar
    "&::-webkit-scrollbar": { width: 3, height: 3 },
    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(120,120,140,0.18)",
      borderRadius: 2,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "rgba(120,120,140,0.3)",
    },
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(120,120,140,0.18) transparent",
  };

  const openAllFields = useCallback((rowIndex: number = 0) => {
    setFullFieldModal({ open: true, rowIndex });
  }, []);

  const headerAddButton = useMemo(() => {
    if (transposed) {
      return (
        <AddFieldButton
          availableFields={allSchemaFields}
          usedFields={[]}
          onAddField={(fieldName) => {
            const ld = localDataRef.current;
            if (ld.length > 0) {
              handleAddFieldRef.current(getRowId(ld[0]), fieldName);
            }
          }}
          label={t("editable.field_short", "Field")}
          tooltip={t("editable.add_field", "Add field")}
          variant={headerButtonVariant}
        />
      );
    }
    if (schema?.fields?.event_type?.choices) {
      return (
        <AddEventButton
          eventTypeChoices={schema.fields.event_type.choices}
          eventTypeCategories={getEventTypeCategories()}
          onCreateEvent={(eventType) => handleCreateEventRef.current(eventType)}
          loading={creatingEvent}
          label={t("editable.event_short", "Event")}
          variant={headerButtonVariant}
        />
      );
    }
    return null;
  }, [transposed, allSchemaFields, schema, creatingEvent, t, headerButtonVariant]);

  useEffect(() => {
    onHeaderAddButtonRef.current?.(headerAddButton);
  }, [headerAddButton]);

  const headerActionsElement = renderHeaderActions
    ? renderHeaderActions({ openAllFields })
    : null;

  const modalRow = localData[fullFieldModal.rowIndex] ?? localData[0];
  const modalRowId = modalRow ? getRowId(modalRow) : 0;

  // Per-lesion image gallery for the Diagnosis detail modal: the Imaging rows
  // whose `cancer` FK points at THIS diagnosis (XXMM disease images are anchored to
  // their specific lesion at sync time). Client-side filter of the already-loaded
  // imaging data (no extra request). Only for the Diagnosis table; empty otherwise.
  const modalLinkedImages = useMemo(() => {
    if (tableType !== "diagnosistable" || !modalRow || !imagingRows?.length) return [];
    const diagId = String(getRowId(modalRow));
    return imagingRows.filter(
      (im) => im?.image_url && String(im.cancer) === diagId
    );
    // getRowId is a stable local helper; modalRowId captures the row identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableType, modalRowId, imagingRows]);

  // Ordered image URLs for the Imaging-panel lightbox: every row that has a
  // resolved `image_url`, in row order. The lightbox pages through this set.
  const imagingImageUrls = useMemo(
    () =>
      showImageColumn
        ? localData
            .map((r) => r.image_url as string | undefined)
            .filter((u): u is string => !!u)
        : [],
    [showImageColumn, localData]
  );

  const fullFieldModalElement = schema?.fields && modalRow ? (
    <FullFieldModal
      open={fullFieldModal.open}
      onClose={() => setFullFieldModal((s) => ({ ...s, open: false, initialSection: undefined }))}
      title={schema.model_name ? normalizeKeyName(schema.model_name.replace(/Table$/, "")) : t("editable.all_fields", "All Fields")}
      fields={allSchemaFields}
      fieldCategories={fieldGroups?.field_categories}
      fieldEventCategories={fieldGroups?.field_event_categories}
      initialSection={fullFieldModal.initialSection}
      data={modalRow}
      rowId={modalRowId}
      linkedImages={modalLinkedImages}
      isLocked={modalRow.is_locked}
      lockedFields={modalRow.locked_fields || []}
      // Per-field "edited/created by user" badges: driven by the table kind, NOT
      // by the presence of the lock toggle. Universal event-style tables (e.g.
      // medicationstatementtable) track per-field ownership without exposing a
      // toggle. See PER_FIELD_PROVENANCE_KINDS (cursor_logs/039, 040).
      perFieldProvenance={hasPerFieldProvenance}
      // Per-field lock TOGGLE only applies to static-like tables (cursor_logs/039);
      // event tables use the row-level lock, so they get no per-field toggle.
      onToggleFieldLock={
        isStaticLike
          ? (fieldName, nextLocked) =>
              handleToggleFieldLock(modalRowId, fieldName, nextLocked)
          : undefined
      }
      onSave={(fieldName, value) => handleCellSave(modalRowId, fieldName, value)}
    />
  ) : null;

  if (data.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        {t("editable.no_data", "No data available")}
      </Typography>
    );
  }

  if (schemaLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Render transposed view (rows = fields, columns = records)
  if (transposed) {
    // For transposed: get all shown fields across all records
    const allShownFields = new Set<string>();
    localData.forEach((row) => {
      getShownFieldsForRow(row).forEach((f) => allShownFields.add(f));
    });
    // Also include usableColumns
    usableColumns.forEach((f) => allShownFields.add(f));
    const transposedFields = Array.from(allShownFields);

    // Group fields by FIELD_CATEGORIES when available, else flat list
    const fieldCategories = fieldGroups?.field_categories;
    type FieldSection = { title: string; keys: string[] };
    const transposedSections: FieldSection[] = (() => {
      if (fieldCategories && Object.keys(fieldCategories).length > 0) {
        const rendered = new Set<string>();
        const out: FieldSection[] = [];
        for (const [catName, catKeys] of Object.entries(fieldCategories)) {
          const keys = catKeys.filter((k) => transposedFields.includes(k) && !rendered.has(k));
          keys.forEach((k) => rendered.add(k));
          if (keys.length > 0) out.push({ title: catName, keys });
        }
        const otherKeys = transposedFields.filter((k) => !rendered.has(k));
        if (otherKeys.length > 0) {
          out.push({ title: t("editable.other_category", "Other"), keys: otherKeys });
        }
        return out;
      }
      return [{ title: "", keys: transposedFields }];
    })();

    const colCount = 1 + localData.length;

    const renderFieldRow = (key: string) => {
      const fieldInfo = schema?.fields[key];
      // Static-like tables hold a SINGLE record (one row per patient). Per-field
      // ownership is tracked in `locked_fields` (cursor_logs/039), so the
      // provenance badge is computed PER FIELD (a locked field == edited by the
      // user) rather than from the record's single row-level `source` — editing
      // one field must not label every field as user-edited.
      const staticRecord = localData[0];
      const recordLocked = staticRecord?.is_locked || false;
      const fieldIsLocked =
        isStaticLike &&
        !!staticRecord &&
        ((staticRecord.locked_fields as string[]) || []).includes(key);
      const recordSource =
        isStaticLike && staticRecord
          ? getStaticFieldIndicator(staticRecord, key)
          : null;
      const staticRecordId = staticRecord ? getRowId(staticRecord) : null;
      return (
        <TableRow key={key}>
          <TableCell sx={{ whiteSpace: "nowrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {/* Source badge + lock on the LEFT of the field label, matching
                  the event table's row controls so static + event are
                  homogeneous. The help "?" stays after the label (events carry
                  it at the column-header level instead, which is fine). */}
              {recordSource && (
                <Tooltip title={recordSource.tooltip} arrow>
                  <Box
                    sx={{
                      width: BADGE_SIZE,
                      height: BADGE_SIZE,
                      borderRadius: "50%",
                      backgroundColor: recordSource.bg,
                      color: recordSource.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 0,
                      flexShrink: 0,
                      border: `1px solid ${recordSource.color}`,
                    }}
                  >
                    {recordSource.icon}
                  </Box>
                </Tooltip>
              )}
              {isStaticLike && staticRecord && !recordLocked && (
                <Tooltip
                  arrow
                  title={
                    fieldIsLocked
                      ? t(
                          "editable.unlock_field",
                          "Unlock field (let AI update it again)"
                        )
                      : t(
                          "editable.lock_field",
                          "Lock field (protect from AI changes)"
                        )
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleToggleFieldLock(
                        staticRecordId as number | string,
                        key,
                        !fieldIsLocked
                      )
                    }
                    sx={{ p: 0.25, flexShrink: 0 }}
                  >
                    {fieldIsLocked ? (
                      <Lock
                        sx={{ fontSize: 14, color: theme.palette.warning.main }}
                      />
                    ) : (
                      <LockOpen
                        sx={{ fontSize: 14, color: "text.disabled" }}
                      />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  color: theme.palette.primary.dark,
                  whiteSpace: "nowrap",
                }}
              >
                {fieldInfo?.verbose_name || normalizeKeyName(key)}
              </Typography>
              {fieldInfo?.help_text && (
                <Tooltip title={fieldInfo.help_text} arrow>
                  <HelpOutline
                    sx={{ fontSize: 12, color: "text.secondary" }}
                  />
                </Tooltip>
              )}
            </Box>
          </TableCell>
          {localData.map((row, i) => {
            const isLocked = row.is_locked || false;
            const rowId = getRowId(row);
            // Per-field lock (cursor_logs/039) — only for static-like tables.
            const fieldLocked =
              isStaticLike && ((row.locked_fields as string[]) || []).includes(key);
            const cellLocked = isLocked || fieldLocked;
            return (
              <TableCell
                key={i}
                sx={
                  cellLocked
                    ? { backgroundColor: "action.hover" }
                    : undefined
                }
              >
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <EditableCell
                      value={row[key]}
                      fieldName={key}
                      fieldInfo={fieldInfo}
                      patientId={row.patient}
                      onSave={(field, value) =>
                        handleCellSave(rowId, field, value)
                      }
                      eventTypeCategories={getEventTypeCategories()}
                      getFieldsForEventType={getFieldsForEventType}
                      onMultiSave={(updates) =>
                        handleMultiCellSave(rowId, updates)
                      }
                      disabled={cellLocked}
                    />
                  </Box>
                </Box>
              </TableCell>
            );
          })}
        </TableRow>
      );
    };

    return (
      <>
      {fullFieldModalElement}
      <TableContainer sx={tableContainerSx}>
        <Table size="small" sx={transposedTableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Typography
                  fontWeight="bold"
                  sx={{ fontSize: "0.8rem", color: theme.palette.primary.dark }}
                >
                  {t("editable.column_field", "Field")}
                </Typography>
              </TableCell>
              {localData.map((row, i) => {
                const sourceInfo = getSourceIndicator(row);
                const rowId = getRowId(row);
                const isLocked = row.is_locked || false;

                return (
                  <TableCell key={i}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography
                        fontWeight="bold"
                        sx={{
                          fontSize: "0.8rem",
                          color: theme.palette.primary.dark,
                        }}
                      >
                        {localData.length > 1 ? `${t("editable.column_value", "Value")} (${i + 1})` : t("editable.column_value", "Value")}
                      </Typography>
                      <Tooltip title={sourceInfo.tooltip}>
                        <Box
                          sx={{
                            width: BADGE_SIZE,
                            height: BADGE_SIZE,
                            borderRadius: "50%",
                            backgroundColor: sourceInfo.bg,
                            color: sourceInfo.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: 0,
                            flexShrink: 0,
                            border: `1px solid ${sourceInfo.color}`,
                          }}
                        >
                          {sourceInfo.icon}
                        </Box>
                      </Tooltip>
                      {/* Whole-row "record" lock — only for event-style tables.
                          Static-like tables use inline per-field locks instead
                          (cursor_logs/039), so we hide this control for them. */}
                      {!isStaticLike && (
                        <Tooltip
                          title={
                            isLocked
                              ? t("editable.unlock_record", "Unlock record")
                              : t("editable.lock_record", "Lock record (prevent AI changes)")
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleToggleLock(rowId, isLocked)}
                            sx={{ p: 0.25 }}
                          >
                            {isLocked ? (
                              <Lock
                                sx={{ fontSize: 14, color: "warning.main" }}
                              />
                            ) : (
                              <LockOpen
                                sx={{ fontSize: 14, color: "text.disabled" }}
                              />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {transposedSections.map((section) => (
              <React.Fragment key={section.title || "_flat"}>
                {section.title && (
                  <TableRow id={`field-category-${section.title.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "").toLowerCase()}`}>
                    <TableCell
                      colSpan={colCount}
                      sx={{
                        py: CELL_PY,
                        px: 1.25,
                        background: `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0.76)} 0%, ${alpha(theme.palette.primary.dark, 0.68)} 100%)`,
                        borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography
                          sx={{
                            textTransform: "uppercase",
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            letterSpacing: "0.05em",
                            color: alpha(theme.palette.common.white, 0.9),
                          }}
                        >
                          {section.title}
                        </Typography>
                        {/* Per-section pencil — opens the same FullFieldModal pre-filtered
                            to this section, so clinicians can deep-link into one block
                            (e.g. "Family History") without expanding the whole table. */}
                        <Tooltip
                          title={t(
                            "editable.edit_section_tooltip",
                            "Edit {section}",
                          ).replace("{section}", section.title)}
                          arrow
                          placement="top"
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              setFullFieldModal({
                                open: true,
                                rowIndex: 0,
                                initialSection: section.title,
                              })
                            }
                            aria-label={t(
                              "editable.edit_section_tooltip",
                              "Edit {section}",
                            ).replace("{section}", section.title)}
                            sx={{
                              p: 0.25,
                              color: alpha(theme.palette.common.white, 0.6),
                              "&:hover": {
                                color: theme.palette.common.white,
                                backgroundColor: alpha(theme.palette.common.white, 0.12),
                              },
                            }}
                          >
                            <EditOutlined sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {section.keys.map(renderFieldRow)}
              </React.Fragment>
            ))}
            {/* Add field / Show all row */}
            <TableRow>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AddFieldButton
                    availableFields={allSchemaFields}
                    usedFields={transposedFields}
                    onAddField={(fieldName) => {
                      if (localData.length > 0) {
                        handleAddField(getRowId(localData[0]), fieldName);
                      }
                    }}
                    label={t("editable.field_short", "Field")}
                    tooltip={t("editable.add_field", "Add field")}
                  />
                  {localData.length > 0 && (
                    <Tooltip title={t("editable.show_all_fields", "Show all fields")} placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={() => setFullFieldModal({ open: true, rowIndex: 0 })}
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: alpha(theme.palette.info.main, 0.1),
                          color: theme.palette.info.main,
                          border: "1px solid transparent",
                          transition: "all 0.25s ease",
                          "&:hover": {
                            backgroundColor: alpha(theme.palette.primary.main, 0.18),
                            color: theme.palette.primary.main,
                            borderColor: alpha(theme.palette.primary.main, 0.4),
                          },
                        }}
                      >
                        <OpenInNew sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              {localData.map((_, i) => (
                <TableCell key={i} />
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      </>
    );
  }

  // Render normal view (rows = records, columns = fields)
  return (
    <>
    {fullFieldModalElement}
    {showImageColumn && (
      <ImageLightbox
        open={imageLightbox.open}
        images={imagingImageUrls}
        index={imageLightbox.index}
        onIndexChange={(i) => setImageLightbox((s) => ({ ...s, index: i }))}
        onClose={() => setImageLightbox((s) => ({ ...s, open: false }))}
      />
    )}
    <TableContainer sx={tableContainerSx}>
      <Table size="small" sx={normalTableSx}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 45, position: "sticky", left: 0, zIndex: 2, bgcolor: "inherit" }}>#</TableCell>
            {showImageColumn && (
              <TableCell sx={{ width: "1%", whiteSpace: "nowrap" }}>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color: theme.palette.primary.dark,
                  }}
                >
                  {t("imaging.image_column", "Image")}
                </Typography>
              </TableCell>
            )}
            {visibleColumns.map((key) => {
              const fieldInfo = schema?.fields[key];
              const isDateCol = key === "event_date";
              return (
                <TableCell key={key} sx={isDateCol ? { whiteSpace: "nowrap", width: "1%" } : undefined}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: theme.palette.primary.dark,
                      }}
                    >
                      {fieldInfo?.verbose_name || normalizeKeyName(key)}
                    </Typography>
                    {fieldInfo?.help_text && (
                      <Tooltip title={fieldInfo.help_text} arrow>
                        <HelpOutline
                          sx={{ fontSize: 12, color: "text.secondary" }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              );
            })}
            <TableCell sx={{ width: "1%", whiteSpace: "nowrap", p: 0, pr: 3, position: "sticky", right: 0, zIndex: 2, bgcolor: "inherit" }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {localData.map((row, rowIndex) => {
            const sourceInfo = getSourceIndicator(row);
            const rowId = getRowId(row);
            const isLocked = row.is_locked || false;

            return (
              <React.Fragment key={rowId}>
                <TableRow
                  id={`event-${rowId}`}
                  data-event-row-id={rowId}
                  hover
                  sx={
                    isLocked ? { backgroundColor: "action.hover" } : undefined
                  }
                >
                  <TableCell sx={{ position: "sticky", left: 0, zIndex: 1, bgcolor: "inherit" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          color: theme.palette.primary.dark,
                        }}
                      >
                        {rowIndex + 1}
                      </Typography>
                      <Tooltip title={sourceInfo.tooltip}>
                        <Box
                          sx={{
                            width: BADGE_SIZE,
                            height: BADGE_SIZE,
                            borderRadius: "50%",
                            backgroundColor: sourceInfo.bg,
                            color: sourceInfo.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: 0,
                            flexShrink: 0,
                            border: `1px solid ${sourceInfo.color}`,
                          }}
                        >
                          {sourceInfo.icon}
                        </Box>
                      </Tooltip>
                      {/* Whole-row "record" lock — event-style tables only.
                          Static-like tables use inline per-field locks instead
                          (cursor_logs/039). Static tables are normally rendered
                          transposed, but we guard here too for completeness. */}
                      {!isStaticLike && (
                        <Tooltip
                          title={
                            isLocked
                              ? t("editable.unlock_record", "Unlock record")
                              : t("editable.lock_record", "Lock record (prevent AI changes)")
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleToggleLock(rowId, isLocked)}
                            sx={{ p: 0.25 }}
                          >
                            {isLocked ? (
                              <Lock
                                sx={{ fontSize: 14, color: "warning.main" }}
                              />
                            ) : (
                              <LockOpen
                                sx={{ fontSize: 14, color: "text.disabled" }}
                              />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={t("editable.delete_event", "Delete event")}>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteConfirm({ open: true, rowIndex, rowId })}
                          sx={{ p: 0.25 }}
                        >
                          <Close sx={{ fontSize: 14, color: "text.disabled" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  {showImageColumn && (
                    <TableCell sx={{ width: "1%", py: 0.5 }}>
                      <ImageThumbnail
                        imageUrl={row.image_url as string | undefined}
                        onOpen={
                          row.image_url
                            ? () => {
                                const idx = imagingImageUrls.indexOf(
                                  row.image_url as string
                                );
                                setImageLightbox({ open: true, index: idx >= 0 ? idx : 0 });
                              }
                            : undefined
                        }
                      />
                    </TableCell>
                  )}
                  {visibleColumns.map((key) => {
                    const effKey = resolveCompactColumnKey(row, key);
                    // When the "Value" column is showing the qualitative result
                    // (`value_text`) instead of the numeric `value`, the header
                    // still reads "Value" — surface the field's real label
                    // ("Qualitative Result") in a Tooltip so the fallback cell
                    // isn't silently mislabeled. No layout change, no new column.
                    const showValueTextHint =
                      tableType === "labresulttable" &&
                      key === "value" &&
                      effKey === "value_text";
                    const valueTextLabel =
                      schema?.fields?.value_text?.verbose_name ||
                      t("labresult.qualitative_result", "Qualitative Result");
                    const cellEl = (
                      <EditableCell
                        value={row[effKey]}
                        fieldName={effKey}
                        fieldInfo={schema?.fields[effKey]}
                        patientId={row.patient}
                        eventTypeCategories={getEventTypeCategories()}
                        getFieldsForEventType={getFieldsForEventType}
                        onMultiSave={(updates) =>
                          handleMultiCellSave(rowId, updates)
                        }
                        onSave={(field, value) =>
                          handleCellSave(rowId, field, value)
                        }
                        disabled={isLocked}
                      />
                    );
                    return (
                    <TableCell key={key} sx={key === "event_date" ? { whiteSpace: "nowrap" } : undefined}>
                      {showValueTextHint ? (
                        <Tooltip title={valueTextLabel} arrow placement="top">
                          <Box sx={{ display: "flex", flex: 1, minWidth: 0 }}>
                            {cellEl}
                          </Box>
                        </Tooltip>
                      ) : (
                        cellEl
                      )}
                    </TableCell>
                    );
                  })}
                  <TableCell sx={{ position: "sticky", right: 0, zIndex: 1, bgcolor: "inherit", py: 0.5, pl: 0.5, pr: 3, textAlign: "center", whiteSpace: "nowrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                      <Tooltip title={t("editable.show_all_fields", "Show all fields")} placement="top" arrow>
                        <IconButton
                          size="small"
                          onClick={() => setFullFieldModal({ open: true, rowIndex })}
                          sx={{
                            width: 24,
                            height: 24,
                            backgroundColor: alpha(theme.palette.info.main, 0.1),
                            color: theme.palette.info.main,
                            border: "1px solid transparent",
                            transition: "all 0.25s ease",
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.18),
                              color: theme.palette.primary.main,
                              borderColor: alpha(theme.palette.primary.main, 0.4),
                            },
                          }}
                        >
                          <OpenInNew sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                      {(expandableColumns.length > 0 ||
                        Object.keys(addedFields).length > 0) && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            setExpandedRow(
                              expandedRow === rowIndex ? null : rowIndex
                            )
                          }
                          sx={{ p: 0.25 }}
                        >
                          {expandedRow === rowIndex ? (
                            <KeyboardArrowUp sx={{ fontSize: 16 }} />
                          ) : (
                            <KeyboardArrowDown sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>

                {/* Expanded row content */}
                {(expandableColumns.length > 0 ||
                  Object.keys(addedFields).length > 0) && (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length + 2 + (showImageColumn ? 1 : 0)}
                      sx={{ py: 0 }}
                    >
                      <Collapse
                        in={expandedRow === rowIndex}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ py: 1, px: 2 }}>
                          {/* Show fields with values + added fields */}
                          {(() => {
                            const rowId = getRowId(row);
                            const shownFields = getShownFieldsForRow(row);
                            const rowAddedFields = addedFields[rowId] || [];

                            const eventType = row.event_type;
                            const relevantFields = eventType
                              ? getFieldsForEventType(eventType)
                              : [];
                            const relevantSet = new Set(relevantFields);

                            const relevantWithValues = relevantFields.filter(
                              (key) =>
                                !visibleColumns.includes(key) &&
                                (shownFields.includes(key) || rowAddedFields.includes(key))
                            );
                            const otherWithValues = expandableColumns.filter(
                              (key) =>
                                !relevantSet.has(key) &&
                                shownFields.includes(key)
                            );

                            const fieldsToShow = [
                              ...relevantWithValues,
                              ...otherWithValues,
                              ...rowAddedFields.filter(
                                (key) =>
                                  !visibleColumns.includes(key) &&
                                  !relevantWithValues.includes(key) &&
                                  !otherWithValues.includes(key)
                              ),
                            ].filter((v, i, a) => a.indexOf(v) === i);

                            return (
                              <>
                                <Table size="small" sx={{ tableLayout: "auto" }}>
                                  <TableBody>
                                    {fieldsToShow.map((key) => {
                                      const fieldInfo = schema?.fields[key];
                                      return (
                                        <TableRow key={key}>
                                          <TableCell
                                            sx={{
                                              whiteSpace: "nowrap",
                                              borderBottom: `1px solid ${theme.palette.divider}`,
                                              py: EXPANDED_CELL_PY,
                                              pl: 0,
                                              pr: 2,
                                            }}
                                          >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                              <Typography
                                                sx={{
                                                  fontSize: "0.8rem",
                                                  fontWeight: 500,
                                                  color: theme.palette.primary.dark,
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                {fieldInfo?.verbose_name || normalizeKeyName(key)}
                                              </Typography>
                                              {fieldInfo?.help_text && (
                                                <Tooltip title={fieldInfo.help_text} arrow>
                                                  <HelpOutline sx={{ fontSize: 12, color: "text.secondary" }} />
                                                </Tooltip>
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              width: "100%",
                                              borderBottom: `1px solid ${theme.palette.divider}`,
                                              py: EXPANDED_CELL_PY,
                                              px: 0,
                                            }}
                                          >
                                            <EditableCell
                                              value={row[key]}
                                              fieldName={key}
                                              fieldInfo={fieldInfo}
                                              patientId={row.patient}
                                              eventTypeCategories={getEventTypeCategories()}
                                              getFieldsForEventType={getFieldsForEventType}
                                              onMultiSave={(updates) =>
                                                handleMultiCellSave(rowId, updates)
                                              }
                                              onSave={(field, value) =>
                                                handleCellSave(rowId, field, value)
                                              }
                                            />
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>

                                {/* Add field + Show all fields */}
                                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                  <AddFieldButton
                                    availableFields={allSchemaFields}
                                    usedFields={[
                                      ...visibleColumns,
                                      ...fieldsToShow,
                                      ...rowAddedFields,
                                    ]}
                                    onAddField={(fieldName) =>
                                      handleAddField(rowId, fieldName)
                                    }
                                    label={t("editable.field_short", "Field")}
                                    tooltip={t("editable.add_field_to_event", "Add field to this event")}
                                  />
                                  <Tooltip title={t("editable.show_all_fields", "Show all fields")} placement="top" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => setFullFieldModal({ open: true, rowIndex })}
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                                        color: theme.palette.info.main,
                                        border: "1px solid transparent",
                                        transition: "all 0.25s ease",
                                        "&:hover": {
                                          backgroundColor: alpha(theme.palette.primary.main, 0.18),
                                          color: theme.palette.primary.main,
                                          borderColor: alpha(theme.palette.primary.main, 0.4),
                                        },
                                      }}
                                    >
                                      <OpenInNew sx={{ fontSize: 13 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </>
                            );
                          })()}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>

      {/* Add Event button */}
      {tableType === "eventtable" &&
        patientId &&
        schema?.fields?.event_type?.choices && (
          <Box sx={{ px: 1, pb: 1 }}>
            <AddEventButton
              eventTypeChoices={schema.fields.event_type.choices}
              eventTypeCategories={getEventTypeCategories()}
              onCreateEvent={handleCreateEvent}
              loading={creatingEvent}
              label={t("editable.event_short", "Event")}
            />
          </Box>
        )}
    </TableContainer>

    {/* Delete event confirmation */}
    <Dialog
      open={deleteConfirm.open}
      onClose={() => setDeleteConfirm({ open: false, rowIndex: -1, rowId: 0 })}
    >
      <DialogTitle>{t("editable.delete_event_title", "Delete event?")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t("editable.delete_event_confirm", "This will permanently delete this event and all its data. This action cannot be undone.")}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteConfirm({ open: false, rowIndex: -1, rowId: 0 })}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={() => handleDeleteEvent(deleteConfirm.rowId, deleteConfirm.rowIndex)}
        >
          {t("common.delete", "Delete")}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default EditableTable;
